import React from 'react';
import { UserIcon, SearchIcon } from '@hero/web-icons';
import type { MockOrder, MockViewerRole } from '../types';
import { EditAffordance } from './EditAffordance';

export interface DriverCardProps {
  order: MockOrder;
  viewer: MockViewerRole;
}

const formatLastSeen = (minutes?: number) => {
  if (minutes === undefined) return null;
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const h = Math.floor(minutes / 60);
  if (h < 24) return `${h} hr ago`;
  return `${Math.floor(h / 24)} d ago`;
};

/**
 * Three states:
 *
 *   1. assigned — driver chip with name, phone, last-seen freshness.
 *   2. searching — "Searching for driver" with subtle spinner; means
 *      the order has been published to the executor's network.
 *   3. unassigned — CTA "Assign driver" button.
 *
 * Only the executing carrier org may assign drivers. Shipper sees
 * read-only view (no pencil).
 */
export const DriverCard: React.FC<DriverCardProps> = ({ order, viewer }) => {
  const canAssign = viewer === 'carrier_org';
  const driver = order.driver;

  let body: React.ReactNode;
  if (driver) {
    const lastSeen = formatLastSeen(driver.lastSeenMinutesAgo);
    body = (
      <div className="mt-3 flex items-start gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 text-gray-600">
          <UserIcon className="size-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center">
            <p className="truncate text-[14px] font-semibold text-gray-900">
              {driver.name}
            </p>
            <EditAffordance canEdit={canAssign} field="driver" />
          </div>
          <p className="mt-0.5 text-[12px] tabular-nums text-gray-500">
            {driver.phone}
          </p>
          {lastSeen && (
            <p className="mt-1 inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700 ring-1 ring-emerald-200">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              Last seen {lastSeen}
            </p>
          )}
        </div>
      </div>
    );
  } else if (order.status === 'searching') {
    body = (
      <div className="mt-3 flex items-start gap-3 rounded-lg bg-blue-50/50 p-3">
        <SearchIcon className="size-4 mt-0.5 text-blue-600 animate-pulse" />
        <div className="text-sm">
          <p className="font-medium text-blue-900">Searching internally</p>
          <p className="mt-0.5 text-[12px] text-blue-700/80">
            Notifying drivers in {order.carrier?.name ?? 'your fleet'}
          </p>
        </div>
      </div>
    );
  } else {
    body = (
      <div className="mt-3">
        <button
          type="button"
          disabled={!canAssign}
          onClick={() => console.log('[orders-preview] open assign-driver')}
          className="w-full rounded-lg border border-dashed border-gray-300 px-3 py-2 text-[13px] font-medium text-gray-600 hover:border-gray-400 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60">
          {canAssign ? 'Assign driver' : 'No driver assigned'}
        </button>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-2">
        <div className="rounded-lg bg-indigo-50 p-1.5">
          <UserIcon className="size-4 text-indigo-600" />
        </div>
        <h4 className="text-[14px] font-semibold text-gray-900">Driver</h4>
      </div>
      {body}
    </div>
  );
};
